// Vejrudsigten: en smal strimmel under HUD'en med de næste fronter og nedtælling i sekunder.
// Turkis, når der er god tid; orange under 5 s og mens fronten er i gang. Aldrig rød: rød hører til
// udfald, brud og mørkeure. Ingen front, ingen strimmel (den fylder ikke noget).

import type { EventKind, ForecastFront, State } from '../sim';
import { forecast, TICK_HZ } from '../sim';
import { HEAT_FACTOR_PM } from '../sim/cities';
import { levelOf } from '../sim/state';
import { SEASON_TICKS } from '../sim/types';
import { DEMAND_PM, seasonIndex, seasonOf } from '../sim/weather';
import { h, setText } from './dom';
import { svg as iconSvg } from './icons';

/** Højst så mange fronter i strimlen (de nærmeste). */
const MAX_FRONTS = 3;
/** Under så mange sekunder bliver brikken orange. */
const SOON_S = 5;

/** Samme ikonsæt som resten af UI'et (icons.ts), bare i lille størrelse. */
const svg = (body: string): string => iconSvg(body, undefined, 18);

/** Et lille ikon pr. slags hændelse (arver tekstfarven). */
const KIND_ICON: Record<EventKind, string> = {
  storm: svg('<path d="M7 15a4 4 0 010-8 5.5 5.5 0 0110.6 1.5A3.3 3.3 0 0117 15z"/><path d="M12.5 13l-2 4h3l-2 4"/>'),
  digger: svg('<path d="M3 18h11v-5H9l-2 3H3z"/><path d="M14 13l4-6 3 2-2 5"/><circle cx="6" cy="19" r="1.4"/><circle cx="11" cy="19" r="1.4"/>'),
  anchor: svg('<circle cx="12" cy="5" r="2"/><path d="M12 7v13M8 11h8M5 14a7 7 0 0014 0"/>'),
  protest: svg('<rect x="5" y="4" width="14" height="9" rx="1.5"/><path d="M12 13v8M9 8h6"/>'),
  spike: svg('<path d="M3 16h4l3-9 4 13 3-7h4"/>'),
  heatwave: svg('<circle cx="12" cy="12" r="4"/><path d="M12 2v3M12 19v3M2 12h3M19 12h3M5 5l2 2M17 17l2 2M5 19l2-2M17 7l2-2"/>'),
  dc_rush: svg('<rect x="5" y="3" width="14" height="7" rx="1.5"/><rect x="5" y="14" width="14" height="7" rx="1.5"/><path d="M9 6.5h.01M9 17.5h.01"/>'),
  plant_trip: svg('<path d="M3 20V11l5 3V11l5 3V6h4v14z"/><path d="M15 3l4 4M19 3l-4 4"/>'),
  icing: svg('<path d="M12 3v18M4.2 7.5l15.6 9M4.2 16.5l15.6-9"/><path d="M9.5 4.5L12 6l2.5-1.5M9.5 19.5L12 18l2.5 1.5"/>'),
  dunkelflaute: svg('<path d="M18 14.5A7 7 0 019.5 6a7 7 0 108.5 8.5z"/><path d="M3 20h18" opacity=".55"/>'),
  cable_half: svg('<path d="M3 12h7M14 12h7"/><path d="M10 9v6M14 9v6"/>'),
  wind_bonanza: svg('<path d="M3 9h11a3 3 0 10-3-3M3 15h15a3 3 0 11-3 3"/>'),
  transformer: svg('<rect x="5" y="6" width="14" height="13" rx="2"/><path d="M12.5 9l-2 4h3l-2 4"/>'),
  smaa: svg('<path d="M4 15c3-1 5-4 9-4 3 0 5 2 7 1-1 3-4 5-8 5-3 0-6-1-8-2z"/><path d="M13 11l2-5"/>'),
  duo: svg('<path d="M13 2L6 13h5l-1 9 7-11h-5z"/><circle cx="4" cy="19" r="1.6"/><circle cx="20" cy="19" r="1.6"/>'),
};

/**
 * Brikkens navn: højst 10 tegn, så navnet altid står på brikken (Spor: kortet: "nu" og "12 s" uden
 * navn sagde ikke, hvad der kom). Det lange navn står i titlen og hos skærmlæseren.
 */
export const KIND_SHORT: Record<EventKind, string> = {
  storm: 'Storm',
  digger: 'Gravko',
  anchor: 'Ankerbåd',
  protest: 'Protest',
  spike: 'Spidsbølge',
  heatwave: 'Hedebølge',
  dc_rush: 'DC-kø',
  plant_trip: 'Værksstop',
  icing: 'Isslag',
  dunkelflaute: 'Vindstille',
  cable_half: 'Kabelskade',
  wind_bonanza: 'Stærk vind',
  transformer: 'Trafofejl',
  smaa: 'Gæst',
  duo: 'Fælles lyn',
};

/** Brikkens tekst: navnet står altid med ("Storm nu", "DC-kø om 12 s", "Gravko udsat"). */
export function chipText(v: FrontView, held = false): string {
  const name = v.kinds[0] ? KIND_SHORT[v.kinds[0]] : 'Uvejr';
  if (v.underWay) return `${name} nu`;
  return held ? `${name} udsat` : `${name} om ${v.secs} s`;
}

/** Navne til skærmlæseren og titlen (kort og på dansk). */
const KIND_NAME: Record<EventKind, string> = {
  storm: 'Storm',
  digger: 'Gravko',
  anchor: 'Ankerbåd',
  protest: 'Naboprotest',
  spike: 'Spidsbølge',
  heatwave: 'Hedebølge',
  dc_rush: 'Datacenter-kø',
  plant_trip: 'Værksnedbrud',
  icing: 'Isslag',
  dunkelflaute: 'Mørkt og vindstille',
  cable_half: 'Kabelskade',
  wind_bonanza: 'Kraftig vind',
  transformer: 'Trafofejl',
  smaa: 'Gæst på ledningen',
  duo: 'Fælles lyn: begge skal handle',
};

export interface FrontView {
  id: string;
  kinds: EventKind[];
  /** Hele sekunder til fronten, 0 når den er i gang. */
  secs: number;
  underWay: boolean;
  soon: boolean;
}

/**
 * Slagsene i en front i fast rækkefølge (KIND_SHORT's), ikke i den rækkefølge truslerne kom: ellers
 * kaldte værten en blandet front "Gravko udsat" og gæsten "Ankerbåd udsat" (Spor: tillid, co-op runde 4).
 */
const KIND_RANK = new Map((Object.keys(KIND_SHORT) as EventKind[]).map((k, i) => [k, i]));
const ordered = (kinds: Iterable<EventKind>): EventKind[] => [...new Set(kinds)].sort((a, b) => (KIND_RANK.get(a) ?? 99) - (KIND_RANK.get(b) ?? 99));

/** Strimlens indhold: de nærmeste fronter med sekunder til start (ren funktion, testbar). */
export function frontViews(state: State, fronts: readonly ForecastFront[] = forecast(state)): FrontView[] {
  const out: FrontView[] = [];
  const toSecs = (tick: number): number => Math.max(0, Math.ceil((tick - state.clockTick) / TICK_HZ));
  for (const f of fronts) {
    if (out.length >= MAX_FRONTS) break;
    if (f.underWay) {
      // En front "i gang" kan være mellem to hændelser. IGN runde 1: "Gravko nu" stod tilbage, selvom
      // intet var på kortet. "nu" kun med en hændelse, der er varslet eller i gang; ellers tæller
      // brikken ned til frontens næste hændelse.
      // Spor: kortet. En gæst, der er jaget væk (eller en ankerbåd, der er vist væk), står stadig
      // "aktiv" et øjeblik i simulationen, men er ingen fare: "Gravko nu" løj.
      const live = state.threats.filter((t) => t.front === f.id && t.phase !== 'done' && !t.shooed).map((t) => t.kind as EventKind);
      if (live.length > 0) {
        // IGN-løkke 2 (runde 2): to fronter med samme slags i gang stod som "Spidsbølge nu /
        // Spidsbølge nu". Det er én fare at se på; den nye slås sammen med den, der allerede står.
        const kinds = ordered(live);
        const twin = out.find((v) => v.underWay && v.kinds[0] === kinds[0]);
        if (twin) {
          twin.kinds = ordered([...twin.kinds, ...kinds]);
          continue;
        }
        out.push({ id: f.id, kinds, secs: 0, underWay: true, soon: true });
        continue;
      }
      const dir = state.director;
      const next = [...dir.planned, ...(dir.next?.planned ?? [])].filter((e) => e.front === f.id).sort((a, b) => a.hitAt - b.hitAt)[0];
      if (!next) continue;
      const secs = toSecs(next.hitAt);
      out.push({ id: f.id, kinds: ordered(f.kinds), secs, underWay: false, soon: secs < SOON_S });
      continue;
    }
    const secs = toSecs(f.startTick);
    out.push({ id: f.id, kinds: ordered(f.kinds), secs, underWay: false, soon: secs < SOON_S });
  }
  return out;
}

/** "Storm om 12 s", "Storm og gravko nu". */
export function frontLabel(v: FrontView): string {
  const names = v.kinds.map((k, i) => (i === 0 ? KIND_NAME[k] : KIND_NAME[k].toLowerCase()));
  const what = names.length <= 1 ? (names[0] ?? 'Uvejr') : `${names.slice(0, -1).join(', ')} og ${names[names.length - 1]}`;
  return v.underWay ? `${what} nu` : `${what} om ${v.secs} s`;
}

export interface WinterView {
  city: string;
  name: string;
  /** Hvor meget mere byerne bruger til vinter, i procent. */
  pct: number;
  /** Hele sekunder til vinter. */
  secs: number;
}

/**
 * Vinterspringet (spor "sandhed", anmelderne: "vinteren kommer bag på én"): i efteråret siger en brik,
 * hvor meget mere strøm byerne skal bruge til vinter, og hvilken by der så ikke får nok, hvis nettet
 * står som nu. Ingen by i fare, ingen brik.
 */
export function winterView(state: State): WinterView | null {
  if (state.phase !== 'playing' || seasonOf(state.clockTick) !== 2) return null;
  const nowPm = DEMAND_PM[2];
  const winterPm = levelOf(state).mechanics.includes('wishes') ? HEAT_FACTOR_PM : DEMAND_PM[3];
  const pct = Math.round((winterPm / nowPm - 1) * 100);
  let best: WinterView | null = null;
  let worst = 0;
  for (const n of state.nodes) {
    const c = n.city;
    if (!c || !n.active || n.phantom || n.kind !== 'city' || c.out || c.demandMw <= 0) continue;
    // Får byen under 90 % af vinterens forbrug, hvis den får det samme som nu?
    const winter = (c.demandMw * winterPm) / nowPm;
    const lack = winter * 0.9 - c.servedMw;
    if (lack <= worst) continue;
    worst = lack;
    best = { city: n.id, name: n.name, pct, secs: 0 };
  }
  if (!best) return null;
  best.secs = Math.max(0, Math.ceil(((seasonIndex(state.clockTick) + 1) * SEASON_TICKS - state.clockTick) / TICK_HZ));
  return best;
}

/** "Til vinter: +20 % i Viborg". */
export function winterText(w: WinterView): string {
  return `Til vinter: +${w.pct} % i ${w.name}`;
}

/** Snefnug (samme streg som isslaget). */
const WINTER_ICON = KIND_ICON.icing;

export class Forecast {
  readonly el = h('div', { class: 'forecast', hidden: true, 'aria-hidden': 'true' });
  /** Skærmlæseren får kun besked, når en front dukker op, bliver nær eller går i gang. */
  readonly live = h('div', { class: 'sr-only', 'aria-live': 'polite', role: 'status' });
  private key = '';
  private said = new Map<string, string>();
  /** Det laveste sekundtal, hver front har vist. */
  private low = new Map<string, number>();
  private lastTick = 0;
  private clearTimer: ReturnType<typeof setTimeout> | undefined;

  update(state: State, show: boolean): void {
    const views = show && state.phase === 'playing' ? frontViews(state) : [];
    const winter = show ? winterView(state) : null;
    // IGN-løkke runde 2: tempodirektøren skubber en front under pres, og så talte "Ankerbåd om 8 s"
    // opad. En nedtælling må aldrig stige: et skub står som "udsat", til tiden er under det, vi viste.
    const held = new Set<string>();
    if (state.clockTick < this.lastTick || state.phase !== 'playing') this.low.clear(); // nyt spil, nyt år
    this.lastTick = state.clockTick;
    for (const v of views) {
      const low = this.low.get(v.id);
      if (v.underWay) this.low.delete(v.id);
      else if (low !== undefined && v.secs > low) held.add(v.id);
      else this.low.set(v.id, v.secs);
    }
    const wkey = winter ? `w:${winter.city}:${winter.pct}:${winter.secs < SOON_S ? 1 : 0}` : '';
    const key = `${wkey}|${views.map((v) => `${v.id}:${held.has(v.id) ? 'h' : v.secs}:${v.soon ? 1 : 0}:${v.kinds.join('+')}`).join('|')}`;
    if (key === this.key) return;
    this.key = key;
    this.el.hidden = views.length === 0 && !winter;
    if (views.length === 0 && !winter) {
      this.el.replaceChildren();
      return;
    }
    // Genbrug brikkerne, så nedtællingen ikke blinker.
    const old = new Map<string, HTMLElement>();
    for (const c of Array.from(this.el.children) as HTMLElement[]) old.set(c.dataset.id ?? '', c);
    const chips = views.map((v) => {
      let c = old.get(v.id);
      // En front uden kendte slags (en spidsbølge midt i en storm) får stormens ikon og "Uvejr nu",
      // ikke et tomt "nu" (playtest runde 3).
      const icons = v.kinds.length > 0 ? v.kinds.slice(0, 2).map((k) => KIND_ICON[k]).join('') : KIND_ICON.storm;
      if (!c || c.dataset.icons !== icons) {
        c = h('div', { class: 'fc-chip', 'data-id': v.id }, h('span', { class: 'fc-ico', html: icons }), h('span', { class: 'fc-s' }));
        c.dataset.icons = icons;
      }
      c.classList.toggle('soon', v.soon);
      c.title = frontLabel(v);
      // I gang: navnet bliver stående ("Storm nu"), så to chips ikke bare står som "nu nu" (playtest runde 2).
      const name = v.kinds[0] ? KIND_NAME[v.kinds[0]] : 'Uvejr';
      // IGN runde 1: et ikon og "12 s" sagde ikke, hvad der kom. Navnet står altid med (det korte navn).
      if (held.has(v.id)) c.title = `${name} er udsat lidt, fordi ${state.coop ? 'I' : 'du'} har travlt.`;
      setText(c.querySelector('.fc-s') as HTMLElement, chipText(v, held.has(v.id)));
      return c;
    });
    if (winter) chips.unshift(this.winterChip(winter, old.get('winter')));
    this.el.replaceChildren(...chips);
    if (winter) this.fitWinter(chips);
    this.announce(views);
    if (winter && this.said.get('winter') !== winter.city) {
      this.said.set('winter', winter.city);
      setText(this.live, `${winterText(winter)}. ${winter.name} får ikke nok, hvis nettet står som nu.`);
      // Også vinterbeskeden er læst op efter få sekunder (IGN-løkke 2, runde 2: "Til vinter" stod til foråret).
      clearTimeout(this.clearTimer);
      this.clearTimer = setTimeout(() => setText(this.live, ''), 4000);
    }
    if (!winter) this.said.delete('winter');
  }

  private fitKey = '';

  /**
   * Står bynavnet ikke helt, falder "Til vinter:" væk (snefnuget siger det stadig), så tallet og byen
   * er det, man ser (break-ui: "Til vinter: +20 % i" uden by på en telefon). Måles kun, når
   * brikkerne, byen eller bredden skifter, ikke hver frame.
   */
  private fitWinter(chips: HTMLElement[]): void {
    const c = chips[0];
    const city = c?.querySelector('.fc-w-city');
    if (!c || !city) return;
    const key = `${innerWidth}|${chips.map((x) => x.dataset.id).join(',')}|${city.textContent}`;
    // Skjult strimmel: intet at måle endnu, prøv igen, når den ses.
    if (key === this.fitKey || c.offsetWidth === 0) return;
    this.fitKey = key;
    c.classList.remove('tight');
    if (city.scrollWidth > city.clientWidth + 1) c.classList.add('tight');
  }

  private winterChip(w: WinterView, old: HTMLElement | undefined): HTMLElement {
    const c =
      old ??
      h(
        'div',
        { class: 'fc-chip fc-winter', 'data-id': 'winter' },
        h('span', { class: 'fc-ico', html: WINTER_ICON }),
        h('span', { class: 'fc-s' }, h('span', { class: 'fc-w-pre' }, 'Til vinter: '), h('span', { class: 'fc-w-what' }), h('span', { class: 'fc-w-city' })),
      );
    c.classList.toggle('soon', w.secs < SOON_S);
    c.title = `${winterText(w)}: ${w.name} får ikke nok til vinter, hvis nettet står som nu. Vinter om ${w.secs} s.`;
    // Skærmlæseren får hele sætningen, også når "Til vinter:" er faldet væk (fitWinter).
    c.setAttribute('aria-label', `${winterText(w)}. Vinter om ${w.secs} s.`);
    setText(c.querySelector('.fc-w-what') as HTMLElement, `+${w.pct} % i `);
    setText(c.querySelector('.fc-w-city') as HTMLElement, w.name);
    return c;
  }

  private announce(views: FrontView[]): void {
    // Én besked pr. trin: på prognosen, under 5 s, i gang. Ikke hvert sekund.
    const lines: string[] = [];
    for (const v of views) {
      const stage = v.underWay ? 'nu' : v.soon ? 'snart' : 'ny';
      if (this.said.get(v.id) === stage) continue;
      this.said.set(v.id, stage);
      lines.push(frontLabel(v));
    }
    if (!lines.length) return;
    setText(this.live, lines.join('. '));
    // Beskeden må ikke blive stående med et gammelt tal (IGN runde 1: skærmlæseren sagde 18 s, brikken 3 s).
    clearTimeout(this.clearTimer);
    this.clearTimer = setTimeout(() => setText(this.live, ''), 4000);
  }

  /** Ny bane: glem, hvad der er sagt. */
  reset(): void {
    this.key = '';
    this.said.clear();
    this.el.hidden = true;
    this.el.replaceChildren();
  }
}
